import { useState, useEffect, useRef } from 'react'
import { comunicadosService } from '../services/api'
import { IconSpeakerphone, IconPaperclip, IconMail, IconBrandWhatsapp, IconFileTypePdf, IconPhoto, IconX, IconBold, IconItalic, IconList } from '@tabler/icons-react'

const PROYECTOS = ['DANA', 'LA NUBE', 'Arbol', 'CASSEA', 'DAHLIA', 'Citrus', 'MAGNOLIA', 'West Retail', 'Área comercial 02', 'UVAH', 'East Retail', 'ACOM', 'Island Retail', 'Mentha Retail']

const textoPlanoAHtml = (texto) => {
  const escapado = texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escapado
    .split(/\n\n+/)
    .map(p => '<p>' + p.replace(/\n/g, '<br>') + '</p>')
    .join('')
}

function EditorEnriquecido({ value, onChange }) {
  const ref = useRef(null)
  const yaInicializado = useRef(false)

  useEffect(() => {
    if (ref.current && !yaInicializado.current && value) {
      ref.current.innerHTML = value
      yaInicializado.current = true
    }
  }, [value])

  const aplicarFormato = (comando) => {
    document.execCommand(comando, false, null)
    ref.current?.focus()
    onChange(ref.current.innerHTML)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6, background: '#F1EFE8', padding: 6, borderRadius: '6px 6px 0 0', border: '1px solid #D4C9B0', borderBottom: 'none' }}>
        <button type="button" onClick={() => aplicarFormato('bold')} className="btn btn-sm" style={{ padding: '4px 8px' }} title="Negrita">
          <IconBold size={14} />
        </button>
        <button type="button" onClick={() => aplicarFormato('italic')} className="btn btn-sm" style={{ padding: '4px 8px' }} title="Cursiva">
          <IconItalic size={14} />
        </button>
        <button type="button" onClick={() => aplicarFormato('insertUnorderedList')} className="btn btn-sm" style={{ padding: '4px 8px' }} title="Lista">
          <IconList size={14} />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={e => onChange(e.currentTarget.innerHTML)}
        style={{
          border: '1px solid #D4C9B0', borderRadius: '0 0 6px 6px', padding: 10,
          minHeight: 140, fontSize: 13, lineHeight: 1.6, background: '#FDFAF4', outline: 'none'
        }}
      />
    </div>
  )
}

export default function Comunicados() {
  const [comunicados, setComunicados] = useState([])
  const [loading, setLoading] = useState(true)
  const [mostrarNuevo, setMostrarNuevo] = useState(false)
  const [instrucciones, setInstrucciones] = useState('')
  const [contenidoGenerado, setContenidoGenerado] = useState('')
  const [editorKey, setEditorKey] = useState(0)
  const [titulo, setTitulo] = useState('')
  const [generando, setGenerando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [publicando, setPublicando] = useState(false)
  const [archivo, setArchivo] = useState(null)
  const [subiendoArchivo, setSubiendoArchivo] = useState(false)
  const [proyecto, setProyecto] = useState('')
  const [canalCorreo, setCanalCorreo] = useState(true)
  const [canalWhatsapp, setCanalWhatsapp] = useState(false)

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setLoading(true)
    try {
      const res = await comunicadosService.listar()
      setComunicados(res.data || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const generarConIA = async () => {
    if (!instrucciones.trim()) return
    setGenerando(true)
    try {
      const res = await comunicadosService.generar(instrucciones)
      setContenidoGenerado(textoPlanoAHtml(res.data.contenido))
      setEditorKey(k => k + 1)
    } catch (err) { console.error(err) }
    finally { setGenerando(false) }
  }

  const seleccionarArchivo = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSubiendoArchivo(true)
    try {
      const res = await comunicadosService.subirArchivo(file)
      setArchivo(res.data)
    } catch (err) {
      console.error(err)
      alert('Error al subir el archivo')
    } finally {
      setSubiendoArchivo(false)
    }
  }

  const guardarComunicado = async () => {
    if (!titulo.trim() || !contenidoGenerado.trim()) return
    setGuardando(true)
    try {
      const canales = [canalCorreo && 'correo', canalWhatsapp && 'whatsapp'].filter(Boolean).join(',') || 'correo'
      await comunicadosService.crear({
        titulo,
        contenido: contenidoGenerado,
        archivoUrl: archivo?.url || null,
        archivoNombre: archivo?.nombre || null,
        archivoTipo: archivo?.tipo || null,
        proyecto: proyecto || null,
        canales
      })
      setMostrarNuevo(false)
      setInstrucciones('')
      setContenidoGenerado('')
      setEditorKey(k => k + 1)
      setTitulo('')
      setArchivo(null)
      setProyecto('')
      setCanalCorreo(true)
      setCanalWhatsapp(false)
      cargar()
    } catch (err) { console.error(err) }
    finally { setGuardando(false) }
  }

  const publicarComunicado = async (id, totalAprox) => {
    const destino = totalAprox || 'todos los residentes activos'
    if (!window.confirm(`¿Publicar y enviar este comunicado a ${destino}? Esta acción no se puede deshacer.`)) return
    setPublicando(true)
    try {
      const res = await comunicadosService.publicar(id)
      alert(res.data.mensaje || 'Comunicado publicado')
      cargar()
    } catch (err) { console.error(err) }
    finally { setPublicando(false) }
  }

  const formatFecha = (date) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const iconoArchivo = (tipo) => {
    if (tipo === 'pdf') return <IconFileTypePdf size={13} />
    if (tipo === 'imagen') return <IconPhoto size={13} />
    return <IconPaperclip size={13} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#8A8A7A' }}>{comunicados.length} comunicados registrados</div>
        <button className="btn btn-primary" onClick={() => setMostrarNuevo(true)}>
          <IconSpeakerphone size={15} /> Nuevo comunicado
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#8A8A7A' }}>Cargando...</div>}
        {!loading && comunicados.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <IconSpeakerphone size={32} color="#D4C9B0" />
            <p style={{ color: '#8A8A7A', marginTop: 12 }}>No hay comunicados aún</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setMostrarNuevo(true)}>
              Crear primer comunicado con IA
            </button>
          </div>
        )}
        {comunicados.map(c => (
          <div key={c.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>{c.titulo}</div>
                <div style={{ fontSize: 12, color: '#8A8A7A', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>Creado por {c.autor?.nombre} · {formatFecha(c.createdAt)}</span>
                  {c.publicadoAt && <span>· Publicado {formatFecha(c.publicadoAt)}</span>}
                  {c.archivoNombre && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#F5EDDD', padding: '1px 6px', borderRadius: 4 }}>
                      {iconoArchivo(c.archivoTipo)} {c.archivoNombre}
                    </span>
                  )}
                  {c.proyecto && <span style={{ background: '#F1EFE8', padding: '1px 6px', borderRadius: 4 }}>{c.proyecto}</span>}
                  {!c.proyecto && <span style={{ background: '#F1EFE8', padding: '1px 6px', borderRadius: 4 }}>Todos los edificios</span>}
                </div>
                <div style={{ fontSize: 12, color: '#5A5A50', lineHeight: 1.6, maxHeight: 80, overflow: 'hidden' }}
                  dangerouslySetInnerHTML={{ __html: c.contenido?.slice(0, 300) }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginLeft: 16, flexShrink: 0 }}>
                <span className={`badge ${c.estado === 'publicado' ? 'badge-resuelto' : 'badge-media'}`}>
                  {c.estado}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(c.canales || 'correo').split(',').includes('correo') && <IconMail size={14} color="#8A8A7A" />}
                  {(c.canales || '').split(',').includes('whatsapp') && <IconBrandWhatsapp size={14} color="#8A8A7A" />}
                </div>
                {c.estado === 'borrador' && (
                  <button className="btn btn-sm btn-primary" onClick={() => publicarComunicado(c.id, c.proyecto)} disabled={publicando}>
                    {publicando ? 'Enviando...' : 'Publicar'}
                  </button>
                )}
                {c.estado === 'publicado' && c._count && (
                  <span style={{ fontSize: 11, color: '#8A8A7A' }}>{c._count.destinatarios} destinatarios</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {mostrarNuevo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="card" style={{ width: 600, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, marginBottom: 20 }}>Nuevo comunicado</div>

            <div className="form-group">
              <label className="form-label">Título del comunicado</label>
              <input className="form-input" placeholder="Ej: Aviso de mantenimiento programado"
                value={titulo} onChange={e => setTitulo(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Instrucciones para el agente IA (opcional)</label>
              <textarea className="form-input" style={{ height: 70, resize: 'none' }}
                placeholder="Ej: Avisar a los residentes que el sábado 5 de julio de 9am a 12pm no habrá agua por mantenimiento de tuberías"
                value={instrucciones} onChange={e => setInstrucciones(e.target.value)} />
              <button className="btn btn-sm" style={{ marginTop: 8, background: '#EAF3DE', color: '#3B6D11', border: 'none' }}
                onClick={generarConIA} disabled={generando}>
                {generando ? '🤖 Generando...' : '🤖 Generar con IA'}
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Contenido — puedes editarlo y darle formato</label>
              <EditorEnriquecido key={editorKey} value={contenidoGenerado} onChange={setContenidoGenerado} />
            </div>

            <div className="form-group">
              <label className="form-label">Archivo adjunto (opcional — PDF, PNG o JPG)</label>
              {!archivo && (
                <div>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={seleccionarArchivo} disabled={subiendoArchivo} />
                  {subiendoArchivo && <div style={{ fontSize: 12, color: '#8A8A7A', marginTop: 4 }}>Subiendo...</div>}
                </div>
              )}
              {archivo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5EDDD', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
                  {iconoArchivo(archivo.tipo)} {archivo.nombre}
                  <button onClick={() => setArchivo(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                    <IconX size={14} />
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">¿A quién se envía?</label>
              <select className="form-input" value={proyecto} onChange={e => setProyecto(e.target.value)}>
                <option value="">Todos los residentes activos</option>
                {PROYECTOS.map(p => <option key={p} value={p}>Solo {p}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Canales de envío</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={canalCorreo} onChange={e => setCanalCorreo(e.target.checked)} />
                  <IconMail size={15} /> Correo electrónico
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#B0A488', cursor: 'not-allowed' }}>
                  <input type="checkbox" checked={false} disabled />
                  <IconBrandWhatsapp size={15} /> WhatsApp — pendiente de aprobación de plantilla por Meta
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn" onClick={() => {
                setMostrarNuevo(false); setContenidoGenerado(''); setEditorKey(k => k + 1); setInstrucciones(''); setTitulo('')
                setArchivo(null); setProyecto(''); setCanalCorreo(true); setCanalWhatsapp(false)
              }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={guardarComunicado} disabled={guardando || !contenidoGenerado}>
                {guardando ? 'Guardando...' : 'Guardar borrador'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
